import { __decorate } from "tslib";
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { localized, msg } from '@lit/localize';
import HelixElement from '../../shared/types/helix-element.js';
import { watch } from '../../shared/directives/watch.decorator.js';
import { alertStyles } from './alert.styles.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import '../icon/icon.js';
import '../button/button.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
/**
 * Alert component to present notifications to a user.
 *
 * @slot [default] – Message for the alert.
 * @slot description – Description text that appears below the message.
 *
 * @event hlx-show - Emitted when the alert appears.
 * @event hlx-hide - Emitted when the alert is closed.
 *
 * @csspart base - The component's base wrapper.
 * @csspart icon - The icon slot container.
 * @csspart body - The body container that wraps message and description.
 * @csspart message - The message slot container.
 * @csspart description - The description slot container.
 */
let HlxAlert = class HlxAlert extends HelixElement {
    constructor() {
        super(...arguments);
        this.message = '';
        this.description = '';
        this.variant = 'brand';
        this.closable = false;
        this.open = true;
        this.spacing = 'default';
        this.slotController = new SlotController(this, 'icon', 'description');
    }
    render() {
        const hasMessage = Boolean(this.message) || this.slotController.hasSlot('[default]');
        const hasDescription = Boolean(this.description) || this.slotController.hasSlot('description');
        return html `
            <div
                part="base"
                class="${classMap({
            'hlx-alert': true,
            'hlx-alert-brand': this.variant === 'brand',
            'hlx-alert-success': this.variant === 'success',
            'hlx-alert-error': this.variant === 'error',
            'hlx-alert-warning': this.variant === 'warning',
            'hlx-alert-info': this.variant === 'info',
            'hlx-alert-condensed': this.spacing === 'condensed',
            'hlx-alert-has-message': hasMessage,
            'hlx-alert-has-description': hasDescription,
        })}"
                role="alert"
                aria-hidden=${this.open ? 'false' : 'true'}>
                <slot name="icon" part="icon" class="hlx-alert-icon">
                    ${this.variant === 'success' ?
            html `
                            <hlx-icon
                                name="circle-check"
                                size="16"
                                fill="${this.getIconColor()}"
                                title="${this.getIconTitle()}"></hlx-icon>
                        `
            : ''}
                    ${(this.variant === 'brand' ||
            this.variant === 'error' ||
            this.variant === 'warning' ||
            this.variant === 'info') ?
            html `
                            <hlx-icon
                                name="circle-exclamation"
                                size="16"
                                fill="${this.getIconColor()}"
                                title="${this.getIconTitle()}"></hlx-icon>
                        `
            : ''}
                </slot>

                <div part="body" class="hlx-alert-body" aria-live="polite">
                    <div class="hlx-alert-message">
                        <slot part="message" class="hlx-alert-message-slot">${this.message}</slot>
                    </div>
                    <div class="hlx-alert-description">
                        <slot
                            name="description"
                            part="description"
                            class="hlx-alert-description-slot">
                            ${this.description}
                        </slot>
                    </div>
                </div>

                <div class="hlx-alert-action">
                    ${this.closable ?
            html `
                            <hlx-button
                                class="hlx-alert-close-button"
                                kind="tertiary"
                                size="small"
                                @click=${this.handleClose}>
                                <hlx-icon
                                    size="16"
                                    name="xmark"
                                    slot="icon"
                                    fill=${this.getIconColor()}
                                    title=${msg('Close icon')}></hlx-icon>
                            </hlx-button>
                        `
            : ''}
                </div>
            </div>
        `;
    }
    firstUpdated() {
        this.base.hidden = !this.open;
    }
    async handleOpenChange() {
        if (this.open) {
            this.emit('hlx-show');
            this.base.hidden = false;
        }
        else {
            this.emit('hlx-hide');
            this.base.hidden = true;
        }
    }
    show() {
        if (this.open) {
            return undefined;
        }
        this.open = true;
    }
    hide() {
        if (!this.open) {
            return undefined;
        }
        this.open = false;
    }
    handleClose(event) {
        event.preventDefault();
        event.stopPropagation();
        this.hide();
    }
    getIconColor() {
        switch (this.variant) {
            case 'brand':
                return 'var(--hlx-color-brand-default)';
            case 'success':
                return 'var(--hlx-color-success-default)';
            case 'error':
                return 'var(--hlx-color-error-default)';
            case 'warning':
                return 'var(--hlx-text-color-default)';
            case 'info':
                return 'var(--hlx-color-info-default)';
            default:
                return '';
        }
    }
    getIconTitle() {
        switch (this.variant) {
            case 'success':
                return msg('Success icon');
            case 'error':
                return msg('Error icon');
            case 'warning':
                return msg('Warning icon');
            case 'info':
                return msg('Info icon');
            default:
                return '';
        }
    }
};
HlxAlert.styles = [componentStyles, alertStyles];
__decorate([
    query('[part~="base"]')
], HlxAlert.prototype, "base", void 0);
__decorate([
    property({ type: String })
], HlxAlert.prototype, "message", void 0);
__decorate([
    property({ type: String })
], HlxAlert.prototype, "description", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxAlert.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxAlert.prototype, "closable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxAlert.prototype, "open", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxAlert.prototype, "spacing", void 0);
__decorate([
    watch('open', { waitUntilFirstUpdate: true })
], HlxAlert.prototype, "handleOpenChange", null);
HlxAlert = __decorate([
    customElement('hlx-alert'),
    localized()
], HlxAlert);
export { HlxAlert };
//# sourceMappingURL=alert.js.map